import { Component, Host, Prop, h, Element } from '@stencil/core';
import { addClass } from '../../utils/helpers/helpers';

@Component({
  tag: 'lgr-button',
  styleUrl: 'button.scss',
  shadow: true,
})
export class Button {
  @Element() el!: HTMLLgrButtonElement;
  /**
   * disabled state
   */
  @Prop({ reflect: true }) disabled = false;
  /**
   * icon: material-icons
   */
  @Prop({ reflect: true }) icon?: string;
  /**
   * size: large or small
   */
  @Prop({ reflect: true }) size?: 'large' | 'small' = 'large';
  /**
   * full width of lgr-button
   */
  @Prop({ reflect: true }) fullWidth: boolean = false;
  /**
   * variant: primary, secondary or text
   */
  @Prop({ reflect: true }) variant?: 'primary' | 'white' | 'secondary' | 'text' = 'primary';
  /**
   * variant: inverse or not. This property has no effect if the variant is set to 'primary'.
   */
  @Prop({ reflect: true }) inverse?: boolean = false;
  /**
   * loading state
   */
  @Prop({ reflect: true }) loading: boolean = false;
  /**
   * The type of the button.
   */
  @Prop() type: 'submit' | 'reset' | 'button' = 'button';
  /**
   * The HTML form element or form element id. Used to submit a form when the button is not a child of the form.
   */
  @Prop() form?: string | HTMLFormElement;
  /**
   * Anchor tag instead of button tag
   */
  @Prop() anchor?: boolean = false;
  /**
   * the URL that the hyperlink points to.
   */
  @Prop() reference?: string = '';
  /**
   * where to display the linked URL
   */
  @Prop() target?: 'self' | 'blank' | 'parent' | 'top' = 'self';
  /**
   * download files
   */
  @Prop() download: boolean = false;

  private tag: HTMLAnchorElement | HTMLButtonElement | undefined;

  private findForm(): HTMLFormElement | null {
    const { form } = this;
    if (form instanceof HTMLFormElement) {
      return form;
    }
    if (typeof form === 'string') {
      const el = document.getElementById(form);
      if (el instanceof HTMLFormElement) {
        return el;
      }
    }
    return null;
  }

  private handleClick = (ev: Event) => {
    const { el } = this;
    if (this.type === 'submit') {
      let formEl = this.findForm();
      const { form } = this;

      if (!formEl && form !== undefined) {
        return;
      }

      if (!formEl) {
        /**
         * If the form element is not set, the button may be inside
         * of a form element. Query the closest form element to the button.
         */
        formEl = el.closest('form');
      }

      if (formEl) {
        ev.preventDefault();

        const fakeButton = document.createElement('button');
        fakeButton.type = this.type;
        fakeButton.style.display = 'none';
        formEl.appendChild(fakeButton);
        fakeButton.click();
        fakeButton.remove();
      }
    }
  };

  componentDidRender() {
    if (this.anchor && this.download) {
      this.tag?.setAttribute('download', '');
    }
  }

  render() {
    const Tag = this.anchor ? 'a' : 'button';
    return (
      <Host onClick={this.handleClick} type={this.type} style={{ pointerEvents: this.disabled || this.loading ? 'none' : undefined }} aria-disabled={this.disabled ? 'true' : null}>
        <Tag
          ref={el => this.tag = el}
          href={Tag === 'a' ? this.reference : undefined}
          target={Tag === 'a' ? `_${this.target}` : undefined}
          disabled={this.disabled}
          class={`${addClass([this.size, this.variant, this.inverse ? 'inverse' : null, this.loading ? 'loading' : null, this.fullWidth ? 'full-width' : null])}`}
        >
          {this.loading && <span class="spinner"></span>}
          {this.icon === 'phone' ? (
            <span class="icon-custom">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.5573 15.6455C12.7517 15.6455 10.9878 15.2427 9.26562 14.4372C7.5434 13.6316 6.01924 12.5655 4.69312 11.2388C3.36646 9.91273 2.29701 8.38856 1.48479 6.66634C0.672014 4.94412 0.265625 3.17329 0.265625 1.35384C0.265625 1.06217 0.362847 0.819119 0.557292 0.624675C0.751736 0.43023 1.00174 0.333008 1.30729 0.333008H4.68229C4.94618 0.333008 5.17535 0.409397 5.36979 0.562175C5.56424 0.714952 5.68229 0.923286 5.72396 1.18717L6.26562 3.99967C6.30729 4.24967 6.30396 4.4719 6.25562 4.66634C6.20674 4.86079 6.09896 5.0344 5.93229 5.18717L3.89063 7.18717C4.44618 8.11773 5.14062 8.99634 5.97396 9.82301C6.80729 10.6491 7.71701 11.3608 8.70312 11.958L10.6823 9.97884C10.849 9.82606 11.0503 9.71495 11.2865 9.64551C11.5226 9.57606 11.7587 9.56217 11.9948 9.60384L14.7448 10.1663C15.0087 10.2358 15.217 10.3608 15.3698 10.5413C15.5226 10.7219 15.599 10.9441 15.599 11.208V14.6247C15.599 14.9163 15.5017 15.1594 15.3073 15.3538C15.1128 15.5483 14.8628 15.6455 14.5573 15.6455ZM2.97396 5.41634L4.34896 4.08301L4.01562 2.22884H2.18229C2.25174 2.77051 2.34563 3.30856 2.46396 3.84301C2.58174 4.37801 2.75174 4.90245 2.97396 5.41634ZM10.4531 12.8955C10.9809 13.1177 11.5156 13.2947 12.0573 13.4263C12.599 13.5586 13.1476 13.6525 13.7031 13.708V11.8955L11.849 11.5205L10.4531 12.8955Z" fill="#183C8F"/>
              </svg>
            </span>
          ) : this.icon === 'close-bold' ? (
            <span class="icon-custom">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.26432 11.8332L0.0976562 10.6665L4.76432 5.99984L0.0976562 1.33317L1.26432 0.166504L5.93099 4.83317L10.5977 0.166504L11.7643 1.33317L7.09766 5.99984L11.7643 10.6665L10.5977 11.8332L5.93099 7.1665L1.26432 11.8332Z" fill="white"/>
              </svg>
            </span>
          ) : (
            this.icon && <lgr-icon class="icon">{this.icon}</lgr-icon>
          )}
          <span class="label">
            <slot></slot>
          </span>
        </Tag>
      </Host>
    );
  }
}
